import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { canonAPI } from '../lib/api';
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Scale,
  MessageSquare,
  Lock,
  Eye,
  RefreshCw
} from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const sectionIcons = {
  'core-identity': Shield,
  'response-structure': MessageSquare,
  'strict-constraints': XCircle,
  'uncertainty': HelpCircle,
  'predictive-refusal': AlertTriangle,
  'free-speech': MessageSquare,
  'emergency': AlertTriangle,
  'self-limitation': Lock,
  'drift-prevention': RefreshCw,
};

export default function RulebookPage() {
  const [rulebook, setRulebook] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    async function loadRulebook() {
      try {
        const response = await canonAPI.getAIRulebook();
        setRulebook(response.data);
      } catch (error) {
        console.error('Failed to load rulebook:', error);
      } finally {
        setLoading(false);
      }
    }
    loadRulebook();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8" data-testid="rulebook-loading">
        <div className="space-y-4">
          {Array(5).fill(0).map((_, i) => (
            <div key={i} className="loading-pulse h-24 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="rulebook-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-2">
          <Lock className="h-4 w-4" />
          <span>{t('rulebook.transparency')}</span>
        </div>
        <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          {t('rulebook.title')}
        </h1>
        <p className="text-muted-foreground">
          {t('rulebook.subtitle')}
        </p>
      </div>

      {/* Purpose Card */}
      <Card className="mb-8 bg-accent/5 border-accent/30">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <Eye className="h-5 w-5 text-accent mt-0.5" />
            <div>
              <h4 className="font-medium">{t('rulebook.fullTransparency')}</h4>
              <p className="text-sm text-muted-foreground">
                {rulebook?.purpose} {t('rulebook.purpose')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sections Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {rulebook?.sections?.map((section) => {
          const Icon = sectionIcons[section.id] || Shield;
          return (
            <Card key={section.id} className="p-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                  <Icon className="h-4 w-4 text-accent" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">{section.title}</h4>
                  <p className="text-xs text-muted-foreground mt-1">{section.summary}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Full Rulebook */}
      <Card data-testid="full-rulebook">
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Lock className="h-5 w-5 mr-2" />
            {t('rulebook.completeRulebook')}
          </CardTitle>
          <CardDescription>
            {t('rulebook.completeRulebookDesc')} {rulebook?.version}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px] pr-4">
            <pre className="text-sm whitespace-pre-wrap font-mono bg-muted/50 p-4 rounded-lg">
              {rulebook?.rulebook}
            </pre>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Key Rules Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        {/* What AI Must Do */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-5 w-5 mr-2" />
              {t('rulebook.mustAlways')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {t('rulebook.mustAlwaysList', { returnObjects: true }).map((item, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="text-green-500 mt-1">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* What AI Must Never Do */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center text-red-600 dark:text-red-400">
              <XCircle className="h-5 w-5 mr-2" />
              {t('rulebook.mustNever')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {t('rulebook.mustNeverList', { returnObjects: true }).map((item, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="text-red-500 mt-1">×</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Note */}
      <Card className="mt-8 bg-muted/30">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <Shield className="h-5 w-5 text-accent mt-0.5" />
            <div>
              <h4 className="font-medium">{t('rulebook.whyMatters')}</h4>
              <p className="text-sm text-muted-foreground">
                {t('rulebook.whyMattersDesc')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
